import format from "date-fns/format";
import parse from "date-fns/parse";
import produce from "immer";

import { Document } from "../../api/documents";
import { sortAlphabetically } from "../../lib/sortUtil";
import { translateDocumentType } from "./translateDocumentType";

interface Filters {
  filterTypeOption: string | null;
  filterYearOption: string | null;
}

interface FilterOption {
  value: string;
  key: string;
}

const DEFAULT_FILTER_OPTION_TYPE = "Alle";
const DEFAULT_FILTER_OPTION_YEAR = "Alle jaren";

const mapToSelectOption = (input: string): FilterOption => ({ value: input, key: input });

const getFilterOptions = (options: string[], defaultOption: string) => {
  const optionsSet = new Set(options);

  if (optionsSet.size < 1) {
    return null;
  }

  return [defaultOption, ...sortAlphabetically("DESC", Array.from(optionsSet))].map(mapToSelectOption);
};

const formatDateToYear = (date: string) => format(parseDocumentDateTime(date), "yyyy");

const parseDocumentDateTime = (date: string) => parse(date, "dd-MM-yyyy", new Date());

const filterDocuments =
  ({ filterTypeOption, filterYearOption }: Filters) =>
  (d: Document) => {
    const formattedDate = formatDateToYear(d.letterDate);
    const hasFilteredType = [DEFAULT_FILTER_OPTION_TYPE, null, d.type].includes(filterTypeOption);
    const hasFilteredYear = [DEFAULT_FILTER_OPTION_YEAR, null, formattedDate].includes(filterYearOption);

    return hasFilteredType && hasFilteredYear;
  };

const sortDocuments = (a: Document, b: Document) => {
  const parsedDateA = parseDocumentDateTime(a.letterDate);
  const parsedDateB = parseDocumentDateTime(b.letterDate);

  return parsedDateB.valueOf() - parsedDateA.valueOf();
};

const getFilteredDocuments = (documents: Document[] | null, { filterTypeOption, filterYearOption }: Filters) =>
  documents?.filter(filterDocuments({ filterTypeOption, filterYearOption })).sort(sortDocuments) ?? [];

interface State {
  loading: boolean;
  documents: Document[] | null;
  filteredDocuments: Document[] | null;
  error?: string;
  filterTypeOption: string | null;
  filterYearOption: string | null;
  filterYearOptions: FilterOption[] | null;
  filterTypeOptions: FilterOption[] | null;
}

export const initialState: State = {
  loading: false,
  documents: null,
  filteredDocuments: null,
  error: undefined,
  filterTypeOption: null,
  filterYearOption: null,
  filterYearOptions: [],
  filterTypeOptions: [],
};

type Action =
  | { type: "FETCH_DATA" }
  | {
      type: "FETCH_SUCCESS";
      payload: { data: { documents: Document[] } };
    }
  | { type: "FETCH_ERROR"; payload: { error: string } }
  | { type: "FILTER_SET_TYPE"; payload: string }
  | { type: "FILTER_SET_YEAR"; payload: string };

export const policyDocumentsReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "FETCH_DATA":
      return produce(state, (draft) => {
        draft.loading = true;
        draft.error = undefined;
      });
    case "FETCH_SUCCESS":
      return produce(state, (draft) => {
        const { documents } = action.payload.data;

        draft.loading = false;
        draft.documents = documents.map((d) => ({
          ...d,
          type: translateDocumentType(d.type),
        }));

        const documentYears = draft.documents.map((d) => formatDateToYear(d.letterDate));
        const documentTypes = draft.documents.map((d) => d.type);

        draft.filterYearOptions = getFilterOptions(documentYears, DEFAULT_FILTER_OPTION_YEAR);
        draft.filterTypeOptions = getFilterOptions(documentTypes, DEFAULT_FILTER_OPTION_TYPE);

        if (draft.filterTypeOptions && draft.filterTypeOptions.length > 0) {
          draft.filterTypeOption = DEFAULT_FILTER_OPTION_TYPE;
        }
        if (draft.filterYearOptions && draft.filterYearOptions.length > 0) {
          draft.filterYearOption = DEFAULT_FILTER_OPTION_YEAR;
        }

        draft.filteredDocuments = getFilteredDocuments(draft.documents, {
          filterTypeOption: draft.filterTypeOption,
          filterYearOption: draft.filterYearOption,
        });
      });
    case "FETCH_ERROR":
      return produce(state, (draft) => {
        draft.loading = false;
        draft.error = action.payload.error;
      });
    case "FILTER_SET_TYPE":
      return produce(state, (draft) => {
        draft.filterTypeOption = action.payload;
        draft.filteredDocuments = getFilteredDocuments(draft.documents, {
          filterTypeOption: draft.filterTypeOption,
          filterYearOption: draft.filterYearOption,
        });
      });
    case "FILTER_SET_YEAR":
      return produce(state, (draft) => {
        draft.filterYearOption = action.payload;
        draft.filteredDocuments = getFilteredDocuments(draft.documents, {
          filterTypeOption: draft.filterTypeOption,
          filterYearOption: draft.filterYearOption,
        });
      });
  }
};
